// music-page/index.ts — the public music page (music.php).
//
// Server half: options/api/music_page_inc.php + ft_music_bucket_tracks() /
// ft_music_bucket_lyrics() in includes/functions/janus/core/music_helpers.php.
//
// RELATIONSHIP TO side-panels-init/music-player.ts — read this before "deduplicating" the two.
// That file is the IN-GAME player. It is welded to campaign state: it polls the encounter
// music resolver, honours a GM broadcast override, takes its master volume from the
// soundboard's `audio_player_music_1` element (whose volume is cookie-persisted by
// template.js), and survives WebSocket frames. None of that exists on a public page with no
// session, which is why this is a separate file rather than an import.
//
// What IS shared, and where the single source of truth lives:
//   * crossfade LENGTH — FT_MUSIC_CROSSFADE_MS in music_helpers.php, handed to both clients in
//     their JSON payload. Tune it there and both players change. Never hardcode it here.
//   * crossfade CURVE — equal-power sin/cos. Mirrored deliberately: it is four lines of
//     arithmetic and a mathematical fact, not a policy that can drift. A linear ramp dips ~3 dB
//     mid-blend, which is the audible hole a long crossfade exists to avoid.
//   * the cull — applied server-side by ft_music_bucket_tracks(), so neither client filters.
//
// A future refactor could lift the transport into a shared module and have music-player.ts
// adopt it. That is a migration of live campaign code and deserves its own verification pass;
// it is deliberately NOT bundled into the change that introduces this page.
//
// WHY TWO <audio> DECKS: a crossfade means two elements playing at once, so one element cannot
// do it and native controls would show two players disagreeing. Same arrangement as in-game.
//
// WHY NOT WEB AUDIO: there is no AudioContext in production, and routing through
// MediaElementSource would need CORS headers on the CDN. An equal-power ramp on
// HTMLAudioElement.volume is entirely adequate for a several-second music fade.

interface MusicTrack {
    audio_id: string;
    audio_name: string;
    // Player-facing song name, e.g. "Journey into the Riverlands (B)" — the version suffix marks
    // which of the two takes Suno returned for one submit. The API falls back to audio_name when
    // a track has not been titled yet, so ALWAYS compare against audio_name before showing it:
    // audio_name is an internal id and reads as a bug to a listener.
    display_name?: string;
    audio_title?: string | null;
    audio_version?: string | null;
    audio_seconds: number | null;
    audio_file: string;
    // Present ONLY in the cross-bucket "Everything" queue, where there is no single playlist
    // name to fall back on for an instrumental row.
    bucket_name?: string;
}

interface MusicSong {
    lyric_id: string;
    song_no: number;
    title: string;
    title_gloss: string;
    language: string;
    language_id: string;
    language_name: string;
    language_url: string;
    body: string;
    translation: string;
    explicit: boolean;
    audio_ids: string[];
}

interface MusicBucketSummary {
    bucket: string;
    name: string;
    tracks: number;
    seconds: number;
    vocal: boolean;
    songs: number;
}

interface MusicGroup {
    key: string;
    label: string;
    accent: string;
    blurb: string;
    buckets: MusicBucketSummary[];
}

interface BucketIndexPayload {
    groups: MusicGroup[];
    total_tracks: number;
    crossfade_ms: number;
}

interface BucketPayload {
    bucket: string;
    name: string;
    tracks: MusicTrack[];
    lyrics: MusicSong[];
    crossfade_ms: number;
    all?: boolean;              // the cross-bucket "Everything" queue
}

const FADE_TICK_MS = 50;
const API = 'options/api/music_page_inc.php';

let groups: MusicGroup[] = [];
let tracks: MusicTrack[] = [];
let songs: MusicSong[] = [];
let songByAudioId = new Map<string, MusicSong>();

let index = -1;
let activeDeck = 0;
let incomingDeck: number | null = null;      // deck fading IN, else null
let crossfadeMs = 6000;
let gain = [1, 0];                            // per-deck crossfade gain, before master
let master = 0.7;
let loopOne = false;
let paused = true;
let currentBucket = '';
// True while the queue is the whole library rather than one bucket. Only changes labelling and
// the URL — playback is identical, because a cross-bucket queue is still just an ordered list.
let allMode = false;

// SHUFFLE IS A PLAY ORDER, NOT A SHUFFLED ARRAY.
//
// `tracks` always stays in the server's order, and `order` is a permutation of its indices that
// the transport walks. Shuffling `tracks` in place instead would renumber the visible track list
// under the listener every time they hit the button, break "track 3 of 22" as a stable reference,
// and leave nothing to restore when shuffle is switched back off.
//
// `index` remains the index into `tracks` throughout, so every paint / lyrics / highlight path
// below is untouched by this feature.
let shuffle = false;
let order: number[] = [];
let cursor = 0;                               // position within `order`

// The one search box drives TWO lists depending on what is on screen: the bucket grid while you
// are browsing, the track list once a queue is open. It used to only ever filter the grid, so
// typing while a playlist was up did nothing at all — the grid it was filtering was hidden.
let trackFilter = '';
// Name to fall back to for an instrumental row in a single-bucket playlist. Held in state
// because the track list is now re-rendered on every keystroke, not just on payload arrival.
let bucketDisplayName = '';
// Which THEME tab the playlist browser is showing. Remembered across visits: somebody who came
// for the songs is probably coming back for the songs.
let activeGroup = '';
// Which tab the lyrics panel is showing. Reset to 'lyrics' when the playlist changes; kept
// across a track change so somebody reading translations is not thrown back every song.
let lyricTab: 'lyrics' | 'translation' = 'lyrics';
// lyric_id currently painted. The paint tick runs twice a second, so the panel is re-rendered
// only when the SONG actually changes — otherwise every tick would blow away the tab selection
// and any text the reader had selected.
let lyricSongId = '';

let fadeTimer: ReturnType<typeof setInterval> | null = null;
let nextTimer: ReturnType<typeof setTimeout> | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;
// Disarms a pending autoplay-unlock retry. The retry is a document-level listener waiting for
// the first gesture, so if playback is stopped before that gesture arrives it has to be torn
// down — otherwise a click made after the listener has gone back to the browser would call
// play() on a deck whose src is still loaded and start hidden audio with no visible player.
let unlockDisarm: (() => void) | null = null;
// Monotonic token for playlist fetches. The last RESPONSE to arrive is not necessarily the
// last one REQUESTED, so a fast second click could be overwritten by a slow first one.
let bucketRequest = 0;

// ── tiny DOM helpers ─────────────────────────────────────────────────────────────────────
const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);
const deckEl = (i: number): HTMLAudioElement | null =>
    document.getElementById(i === 0 ? 'ft_music_deck_a' : 'ft_music_deck_b') as HTMLAudioElement | null;

function esc(s: string): string {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
}

function fmtTime(s: number): string {
    if (!isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const r = Math.floor(s - m * 60);
    return m + ':' + (r < 10 ? '0' : '') + r;
}

/** "1 hr 12 min" / "8 min" — a bucket's total runtime, so the size of a playlist is legible. */
function fmtRuntime(sec: number): string {
    const mins = Math.round(sec / 60);
    if (mins < 60) return mins + ' min';
    const h = Math.floor(mins / 60);
    const m = mins - h * 60;
    return h + ' hr' + (m ? ' ' + m + ' min' : '');
}

// ── play order ───────────────────────────────────────────────────────────────────────────
/**
 * Rebuild `order` for the current `tracks`, and point `cursor` at `keep`.
 *
 * @param keep index into `tracks` that must stay CURRENT, or -1 to start from the top.
 *
 * With shuffle on, `keep` is moved to the front of the shuffled order rather than being left
 * wherever it landed. That is what makes the toggle safe to hit mid-song: the track you are
 * listening to keeps playing and only what comes AFTER it changes. Hitting it again restores
 * the canonical order with the same track still current.
 */
function buildOrder(keep: number): void {
    const n = tracks.length;
    order = [];
    for (let i = 0; i < n; i++) order.push(i);

    if (shuffle && n > 1) {
        // Fisher-Yates. Not a "sort by random()" — that one is biased and, worse, non-transitive,
        // which some engines punish with an exception rather than a merely-wrong order.
        for (let i = n - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            const t = order[i] as number;
            order[i] = order[j] as number;
            order[j] = t;
        }
        if (keep >= 0 && keep < n) {
            const at = order.indexOf(keep);
            if (at > 0) {
                order.splice(at, 1);
                order.unshift(keep);
            }
            cursor = 0;
            return;
        }
        cursor = 0;
        return;
    }

    cursor = (keep >= 0 && keep < n) ? keep : 0;
}

/** Keep `cursor` pointing at whatever `index` is, e.g. after a click on a track row. */
function syncCursor(): void {
    if (order.length !== tracks.length) { buildOrder(index); return; }
    const at = order.indexOf(index);
    cursor = at >= 0 ? at : 0;
}

// ── audio transport ──────────────────────────────────────────────────────────────────────
function applyGain(): void {
    for (let i = 0; i < 2; i++) {
        const d = deckEl(i);
        if (!d) continue;
        const g = Math.max(0, Math.min(1, gain[i] ?? 0));
        d.volume = Math.max(0, Math.min(1, g * master));
    }
}

function clearTimers(): void {
    if (fadeTimer !== null) { clearInterval(fadeTimer); fadeTimer = null; }
    if (nextTimer !== null) { clearTimeout(nextTimer); nextTimer = null; }
}

/**
 * Autoplay: a deck the user has never interacted with may reject play(). Arm a one-shot
 * gesture listener and retry rather than silently never starting.
 */
function play(deck: HTMLAudioElement): void {
    try {
        const p = deck.play() as unknown as Promise<void> | undefined;
        if (p && typeof p.catch === 'function') {
            p.catch((err: unknown) => {
                // ONLY NotAllowedError IS AN AUTOPLAY BLOCK. play() also rejects with AbortError
                // when WE pause the deck before its play promise settles — which happens on every
                // ordinary interruption, because a track click or a Next during a fade calls
                // settleFade()/crossfadeTo() and those pause the outgoing deck. Treating that as
                // "blocked" armed a document-level gesture listener that then re-started a deck
                // the fade had already retired: two songs at once, and a second AbortError when
                // the next pause landed on it.
                //
                // Everything else (NotSupportedError from a 404/undecodable file, a network
                // failure) is a genuine media failure that no amount of gesture will fix, and
                // arming for it would re-arm on every click forever. If some engine is ever found
                // signalling a block by another name, THIS is the line to widen — nothing below
                // it assumes the reason.
                const name = (err && typeof err === 'object' && 'name' in err)
                    ? String((err as { name: unknown }).name) : '';
                if (name !== 'NotAllowedError') return;

                if (unlockDisarm) return;
                const retry = () => {
                    unlockDisarm = null;
                    document.removeEventListener('click', retry, true);
                    document.removeEventListener('keydown', retry, true);

                    // DEFERRED BY ONE TASK, deliberately. This listener is in the CAPTURE phase
                    // (so a handler that stops propagation cannot swallow the gesture), which
                    // means on a track-row click it runs BEFORE the page's own click handler has
                    // moved to that track. Retrying here would start the OLD deck, and the
                    // crossfade the click then triggers would fade that unwanted song out over
                    // six audible seconds. Waiting a task lets playback state settle first.
                    //
                    // Safe for autoplay: media unblocking keys off STICKY activation, which
                    // persists for the document once a gesture has happened — unlike transient
                    // activation, it does not expire at the end of the task.
                    setTimeout(() => {
                        // Re-checked HERE rather than on the way in: the click we deferred past
                        // may itself have stopped playback or gone back to the browser.
                        if (paused || !tracks.length) return;
                        // Whatever is live NOW — which after a track click is the deck the
                        // listener actually asked for. Falls back to the captured deck.
                        const live = deckEl(incomingDeck ?? activeDeck) || deck;
                        // Back through this same wrapper, NOT a bare live.play(): a retry that is
                        // still blocked has to arm the next gesture, and calling play() directly
                        // would swallow the rejection and leave the page permanently silent with
                        // nothing listening for the gesture that would fix it.
                        play(live);
                    }, 0);
                };
                unlockDisarm = () => {
                    document.removeEventListener('click', retry, true);
                    document.removeEventListener('keydown', retry, true);
                    unlockDisarm = null;
                };
                document.addEventListener('click', retry, true);
                document.addEventListener('keydown', retry, true);
            });
        }
    } catch (e) { /* never throw from playback */ }
}

/** Equal-power crossfade from the live deck to `to`, then advance bookkeeping. */
function crossfadeTo(to: number, startAt: number): void {
    clearTimers();
    const from = activeDeck;
    const steps = Math.max(1, Math.round(crossfadeMs / FADE_TICK_MS));
    let step = 0;

    const toDeck = deckEl(to);
    if (!toDeck) return;
    incomingDeck = to;
    try { toDeck.currentTime = startAt; } catch (e) { /* not seekable yet; starts at 0 */ }
    gain[to] = 0;
    applyGain();
    play(toDeck);

    fadeTimer = setInterval(() => {
        step++;
        const p = Math.min(1, step / steps);
        // Equal power: sin/cos hold perceived loudness ~constant across the blend. A linear
        // ramp dips ~3 dB in the middle — the audible hole between tracks.
        gain[to] = Math.sin((p * Math.PI) / 2);
        gain[from] = Math.cos((p * Math.PI) / 2);
        applyGain();

        if (p >= 1) {
            if (fadeTimer !== null) { clearInterval(fadeTimer); fadeTimer = null; }
            const fromDeck = deckEl(from);
            if (fromDeck) { try { fromDeck.pause(); } catch (e) { /* ignore */ } }
            gain[from] = 0;
            applyGain();
            activeDeck = to;
            incomingDeck = null;
            scheduleNext();
            paint();
        }
    }, FADE_TICK_MS);
}

/**
 * Finish any in-flight crossfade INSTANTLY and collapse the two-deck state back to one.
 *
 * Everything that interrupts playback has to call this first. Mid-fade the state is genuinely
 * ambiguous — two decks are audible, `activeDeck` still points at the OUTGOING one, and
 * `incomingDeck` is non-null — and every interruption handled that state wrong:
 *   * pause paused only ONE deck, so the outgoing track kept playing under a "paused" player;
 *   * `incomingDeck` was left non-null forever, and since the 'ended' fallback bails while it
 *     is set, the playlist would never advance again after that pause;
 *   * switching playlists mid-fade reused the incoming deck as the "free" one and crossfaded
 *     from a deck that was itself still fading.
 * Settling first makes all of those single-deck problems.
 */
function settleFade(): void {
    if (fadeTimer !== null) { clearInterval(fadeTimer); fadeTimer = null; }
    if (incomingDeck === null) return;

    const to = incomingDeck;
    const from = to === 0 ? 1 : 0;
    const fromDeck = deckEl(from);
    if (fromDeck) { try { fromDeck.pause(); } catch (e) { /* ignore */ } }
    gain[from] = 0;
    gain[to] = 1;
    activeDeck = to;
    incomingDeck = null;
    applyGain();
}

/** Arm the handoff to the next track at (duration - crossfade). */
function scheduleNext(): void {
    if (nextTimer !== null) { clearTimeout(nextTimer); nextTimer = null; }
    // Never arm the handoff while stopped. Seeking, restarting with Prev, and toggling
    // repeat-one all call this, and while paused an armed timer would start the next track on
    // its own — the player resuming itself with nobody having pressed play.
    if (paused) return;
    if (tracks.length < 2 && !loopOne) return;

    const deck = deckEl(activeDeck);
    const track = tracks[index];
    if (!deck || !track) return;

    // audio_seconds comes from the MusicForge bucket manifest, so the handoff can be scheduled
    // without waiting on loadedmetadata. Fall back to the element if it is ever missing.
    const dur = (track.audio_seconds && track.audio_seconds > 0)
        ? track.audio_seconds
        : (isFinite(deck.duration) && deck.duration > 0 ? deck.duration : 0);
    if (dur <= 0) return;                        // 'ended' is the safety net

    const lead = Math.max(1000, (dur * 1000) - crossfadeMs - (deck.currentTime * 1000));
    nextTimer = setTimeout(() => advance(), lead);
}

/** Load whatever `index` points at onto the free deck and crossfade to it. */
function playCurrent(): void {
    // Settle FIRST. Without this, a manual advance during a crossfade picked the free deck from
    // `incomingDeck` while crossfadeTo() took its `from` from `activeDeck` — mid-fade those are
    // different decks, so `to` could equal `from`. The fade then ramped both gains on ONE deck,
    // paused the track it had just started, and left the previous incoming deck playing
    // untracked. Reachable from Next, Prev and any track-row click during a fade.
    settleFade();
    const next = tracks[index];
    const fromIdx = activeDeck;
    const toIdx = fromIdx === 0 ? 1 : 0;
    const toDeck = deckEl(toIdx);
    if (!toDeck || !next) return;

    toDeck.loop = false;
    if (toDeck.src !== next.audio_file) toDeck.src = next.audio_file;
    paused = false;
    crossfadeTo(toIdx, 0);
    paint();
}

/**
 * @param step explicit user navigation (+1 Next, -1 Prev). Omitted = AUTOMATIC advance, which
 *             is the only kind repeat-one is allowed to pin in place. Repeat-one that also
 *             swallowed Next and Prev made the two buttons look broken while it was on.
 */
function advance(step?: number): void {
    if (!tracks.length) return;
    const n = tracks.length;
    // Step through the PLAY ORDER, not through `tracks` — that is the whole of shuffle. With
    // shuffle off `order` is the identity, so this is the old behaviour exactly.
    if (order.length !== n) buildOrder(index);
    const delta = (typeof step === 'number') ? step : (loopOne ? 0 : 1);
    cursor = ((cursor + delta) % n + n) % n;
    index = order[cursor] ?? 0;
    playCurrent();
}

function startPlaylist(): void {
    // Settle BEFORE picking the free deck: mid-fade, `activeDeck` is the outgoing one, so the
    // "other" deck is the one currently fading in — starting the new playlist on it would
    // crossfade from a deck that is itself still ramping.
    settleFade();
    clearTimers();
    // -1: a fresh playlist has no track worth preserving, so shuffle gets to pick the opener.
    // Starting a shuffled playlist on track 1 every time is the classic tell of a shuffle that
    // only kicks in from the second song.
    buildOrder(-1);
    index = order[0] ?? 0;
    const other = activeDeck === 0 ? 1 : 0;
    const deck = deckEl(other);
    const track = tracks[index];
    if (!deck || !track) return;
    deck.loop = tracks.length === 1;
    deck.src = track.audio_file;
    paused = false;
    crossfadeTo(other, 0);
    paint();
}

function stopAll(): void {
    clearTimers();
    if (unlockDisarm) unlockDisarm();
    incomingDeck = null;
    for (let i = 0; i < 2; i++) {
        const d = deckEl(i);
        if (!d) continue;
        try { d.pause(); } catch (e) { /* ignore */ }
        gain[i] = i === 0 ? 1 : 0;
    }
    applyGain();
    paused = true;
    paint();
}

function togglePlay(): void {
    if (!tracks.length) return;
    if (index < 0) { startPlaylist(); return; }

    if (paused) {
        const live = deckEl(activeDeck);
        if (!live) return;
        paused = false;
        play(live);
        scheduleNext();
    } else {
        paused = true;
        // Collapse any running crossfade to a single deck, THEN pause it. Without the settle,
        // only one of the two audible decks stopped.
        settleFade();
        // Pausing must also disarm the handoff, or the crossfade fires while stopped and the
        // next track starts itself.
        clearTimers();
        const live = deckEl(activeDeck);
        if (live) { try { live.pause(); } catch (e) { /* ignore */ } }
    }
    paint();
}

function seekFraction(f: number): void {
    // Seeking mid-fade would move only the incoming deck while the outgoing one played on from
    // its own position — two tracks, two places. Settle to one deck first.
    settleFade();
    const deck = deckEl(activeDeck);
    const track = tracks[index];
    if (!deck || !track) return;
    const dur = (track.audio_seconds && track.audio_seconds > 0)
        ? track.audio_seconds
        : (isFinite(deck.duration) ? deck.duration : 0);
    if (!(dur > 0)) return;
    try { deck.currentTime = Math.max(0, Math.min(dur - 0.25, f * dur)); } catch (e) { return; }
    // The handoff was armed against the OLD position; re-arm or the crossfade fires early
    // (seek backwards) or never (seek forwards past the trigger).
    scheduleNext();
    paint();
}

// ── rendering ────────────────────────────────────────────────────────────────────────────
function paint(): void {
    const deck = deckEl(incomingDeck ?? activeDeck);
    const track = tracks[index];

    const playIcon = $('[data-ft-music-playicon]');
    if (playIcon) playIcon.innerHTML = paused ? '&#9658;' : '&#10074;&#10074;';

    const titleEl = $('[data-ft-music-title]');
    const subEl = $('[data-ft-music-sub]');
    if (track) {
        const song = songByAudioId.get(track.audio_id);
        // A sung track shows its SONG title; an instrumental one has no title of its own, so
        // it shows the playlist and its position rather than the raw `battle_07` filename,
        // which is an internal id and reads as a bug to a listener.
        if (song) {
            if (titleEl) {
                titleEl.innerHTML = esc(song.title)
                    + (song.title_gloss ? ' <span class="text-muted">“' + esc(song.title_gloss) + '”</span>' : '')
                    + (song.explicit ? ' <span class="badge bg-danger ft-music-explicit">Explicit</span>' : '');
            }
            if (subEl) {
                // Was "High Elvish · female, fast". The voice/tempo half was generator direction
                // and has been dropped everywhere it appeared, so the position takes its place —
                // which is what a transport sub-line is for, and matches the instrumental branch.
                // The language is the CATALOG one for the same reason the tab is: the transport
                // and the tab six inches below it must not name the same tongue two ways.
                subEl.textContent = [song.language_name || song.language,
                                     'track ' + (index + 1) + ' of ' + tracks.length]
                    .filter(Boolean).join(' · ');
            }
        } else if (track.display_name && track.display_name !== track.audio_name) {
            // A titled instrumental: show the song name, exactly like a sung one. The playlist
            // moves to the sub-line, where the position already lives.
            if (titleEl) titleEl.textContent = track.display_name;
            if (subEl) {
                subEl.textContent = [track.bucket_name || currentBucketName(),
                                     'track ' + (index + 1) + ' of ' + tracks.length]
                    .filter(Boolean).join(' · ');
            }
        } else if (track.bucket_name) {
            // Everything queue: the number is a position in a 585-track queue, so "Elvish 347"
            // reads as a track called 347. The playlist name alone is the honest label, and the
            // position belongs on the sub-line where it is explicitly labelled as one.
            if (titleEl) titleEl.textContent = track.bucket_name;
            if (subEl) subEl.textContent = 'track ' + (index + 1) + ' of ' + tracks.length;
        } else {
            if (titleEl) titleEl.textContent = currentBucketName() + ' ' + (index + 1);
            if (subEl) subEl.textContent = currentBucketName() + ' · track ' + (index + 1) + ' of ' + tracks.length;
        }
    } else {
        if (titleEl) titleEl.innerHTML = '&nbsp;';
        if (subEl) subEl.innerHTML = '&nbsp;';
    }

    // progress
    const dur = track && track.audio_seconds && track.audio_seconds > 0
        ? track.audio_seconds
        : (deck && isFinite(deck.duration) ? deck.duration : 0);
    const cur = deck ? deck.currentTime : 0;
    const fill = $('[data-ft-music-progress-fill]');
    if (fill) fill.style.width = (dur > 0 ? Math.max(0, Math.min(100, (cur / dur) * 100)) : 0) + '%';
    const el = $('[data-ft-music-elapsed]');
    if (el) el.textContent = fmtTime(cur);
    const dl = $('[data-ft-music-duration]');
    if (dl) dl.textContent = fmtTime(dur);

    // repeat-one + shuffle state. aria-pressed as well as the class: these are toggles, and a
    // screen reader has no way to perceive "the button looks lit".
    const rep = document.querySelector('[data-ft-music-action="repeat"]');
    if (rep) {
        rep.classList.toggle('active', loopOne);
        rep.setAttribute('aria-pressed', loopOne ? 'true' : 'false');
    }
    const shuf = document.querySelector('[data-ft-music-action="shuffle"]');
    if (shuf) {
        shuf.classList.toggle('active', shuffle);
        shuf.setAttribute('aria-pressed', shuffle ? 'true' : 'false');
    }
    const all = document.querySelector('[data-ft-music-action="shuffle-all"]');
    if (all) {
        all.classList.toggle('active', allMode);
        all.setAttribute('aria-pressed', allMode ? 'true' : 'false');
    }

    // track list + lyrics highlight
    document.querySelectorAll('[data-ft-music-track-index]').forEach((n) => {
        const i = parseInt(n.getAttribute('data-ft-music-track-index') || '-1', 10);
        n.classList.toggle('ft-music-track-current', i === index);
    });
    // Follow the track: repaint the lyrics panel only when the SONG changed. Two takes of the
    // same song share a lyric_id, so advancing between them correctly leaves the panel alone.
    const liveSong = track ? songByAudioId.get(track.audio_id) : undefined;
    const wantId = liveSong ? liveSong.lyric_id : (songs[0] ? songs[0].lyric_id : '');
    if (wantId !== lyricSongId) {
        lyricSongId = wantId;
        renderLyricPanel();
    }
}

function currentBucketName(): string {
    const el = $('[data-ft-music-bucket-name]');
    return el ? (el.textContent || '') : '';
}

/** One playlist card. Shared by the tab view and the search view so the two cannot drift. */
function renderCard(b: MusicBucketSummary, accent: string): string {
    // Counts are ints server-side, but the TS types are a compile-time claim about JSON, not a
    // runtime guarantee — these two are the only values interpolated without esc(), so they get
    // coerced to finite integers instead.
    const nTracks = Math.max(0, Math.trunc(Number(b.tracks) || 0));
    const nSongs = Math.max(0, Math.trunc(Number(b.songs) || 0));
    return '<button type="button" class="ft-music-card" data-ft-music-bucket="' + esc(b.bucket) + '"'
         + ' style="--ft-music-accent:' + esc(accent) + '">'
         // Decorative: the whole card is the button, so the glyph must not be announced as a
         // second control. It exists to say "this plays" on hover, which a bare panel does not.
         + '<span class="ft-music-card-play" aria-hidden="true">&#9658;</span>'
         + '<span class="ft-music-card-body">'
         + '<span class="ft-music-card-name">' + esc(b.name) + '</span>'
         + '<span class="ft-music-card-meta">' + nTracks + ' track' + (nTracks === 1 ? '' : 's')
         + ' · ' + esc(fmtRuntime(b.seconds)) + '</span>'
         + (nSongs > 0 ? '<span class="ft-music-card-tag">' + nSongs + ' song' + (nSongs === 1 ? '' : 's') + ' with words</span>' : '')
         + '</span>'
         + '</button>';
}

/**
 * The playlist browser: a TAB per theme, not six stacked grids in one long scroll.
 *
 * 49 playlists down a single column meant the headers did all the work of telling you where you
 * were, and you had to scroll past four themes to reach the fifth. One theme at a time is a
 * comfortable screen (the largest group is 14), and the themes become navigation instead of
 * milestones you pass.
 *
 * SEARCH OVERRIDES THE TABS. A query is a question about the whole soundtrack, not about the
 * tab you happen to be on, so results span every theme and the tab bar goes quiet — otherwise
 * searching "tavern" from the Battle tab would truthfully report nothing and read as broken.
 */
function renderGroups(filter: string): void {
    const host = $('[data-ft-music-groups]');
    if (!host) return;
    const nav = $('[data-ft-music-groupnav]');
    const blurb = $('[data-ft-music-groupblurb]');
    const q = filter.trim().toLowerCase();
    const searching = q.length > 0;

    // A group the payload no longer has (renamed, emptied) must not leave the browser blank.
    if (!groups.some((g) => g.key === activeGroup)) activeGroup = groups[0] ? groups[0].key : '';

    const hitsIn = (g: MusicGroup) => g.buckets.filter((b) =>
        !q || b.name.toLowerCase().includes(q) || b.bucket.toLowerCase().includes(q)
           || g.label.toLowerCase().includes(q));

    // ── the tab bar ───────────────────────────────────────────────────────────────────────
    if (nav) {
        nav.innerHTML = groups.map((g, gi) => {
            const on = !searching && g.key === activeGroup;
            // Roving tabindex: a tablist is ONE tab stop, then arrow keys move within it —
            // otherwise Tab has to be pressed six times to get past the themes. While searching
            // nothing is selected, so the first tab holds the stop rather than none of them.
            const stop = on || (searching && gi === 0);
            return '<button type="button" role="tab" data-ft-music-group="' + esc(g.key) + '"'
                 + ' class="ft-music-tabnav-btn' + (on ? ' active' : '') + '"'
                 + ' aria-selected="' + (on ? 'true' : 'false') + '"'
                 + ' tabindex="' + (stop ? '0' : '-1') + '"'
                 + ' style="--ft-music-accent:' + esc(g.accent) + '">'
                 + esc(g.label)
                 + '<span class="ft-music-tabnav-n">' + g.buckets.length + '</span>'
                 + '</button>';
        }).join('');
        // Dimmed while searching so it is visibly NOT what is selecting the results.
        nav.classList.toggle('ft-music-tabnav--idle', searching);
    }

    let shown = 0;

    if (searching) {
        if (blurb) blurb.style.display = 'none';
        const parts: string[] = [];
        for (const g of groups) {
            const hits = hitsIn(g);
            if (!hits.length) continue;
            shown += hits.length;
            // The theme still labels its results — a flat list of matches from six themes loses
            // the only thing that tells you what kind of music each one is.
            parts.push('<div class="ft-music-resultgroup">'
                + '<h2 class="ft-music-resultgroup-head" style="--ft-music-accent:' + esc(g.accent) + '">'
                + esc(g.label) + '</h2>'
                + '<div class="ft-music-grid">'
                + hits.map((b) => renderCard(b, g.accent)).join('')
                + '</div></div>');
        }
        host.innerHTML = parts.join('');
    } else {
        const g = groups.find((x) => x.key === activeGroup);
        if (blurb) {
            blurb.textContent = g ? g.blurb : '';
            blurb.style.display = g && g.blurb ? '' : 'none';
        }
        const hits = g ? g.buckets : [];
        shown = hits.length;
        host.innerHTML = g
            ? '<div class="ft-music-grid" style="--ft-music-accent:' + esc(g.accent) + '">'
              + hits.map((b) => renderCard(b, g.accent)).join('') + '</div>'
            : '';
    }

    const status = $('[data-ft-music-status]');
    if (status) {
        if (!shown) {
            status.textContent = q ? 'Nothing matches “' + filter + '”.' : 'No music available.';
            status.style.display = '';
        } else {
            status.style.display = 'none';
        }
    }
}

function renderBucket(payload: BucketPayload): void {
    const nameEl = $('[data-ft-music-bucket-name]');
    if (nameEl) nameEl.textContent = payload.name;
    // The sub-line under the title (count + runtime, or the match count while filtering) is
    // owned by renderTrackList — it is a statement about what is in the list below it, and two
    // writers would disagree the moment a filter is applied.
    bucketDisplayName = payload.name;
    renderTrackList();

    // The lyrics COLUMN is shown for sung buckets; its contents are painted per-track by
    // renderLyricPanel(), not here — the panel follows what is playing.
    const col = $('[data-ft-music-lyrics-col]');
    if (col) col.style.display = payload.lyrics.length ? '' : 'none';
    lyricTab = 'lyrics';
    lyricSongId = '';           // force the next paint to render the panel for this bucket
    renderLyricPanel();
}

/**
 * The track list, filtered by whatever is in the search box.
 *
 * ⚠ FILTERING IS A VIEW CONCERN ONLY. `tracks` and the play `order` are untouched, so searching
 * never changes what plays next — it only changes what you can SEE and click. A filter that
 * quietly re-cut the queue would be a genuinely nasty bug: you would type, walk away, and the
 * player would be somewhere else entirely.
 *
 * Rows keep their ORIGINAL index and their original position number, so a filtered list still
 * says "7" for track 7 rather than renumbering the survivors 1..n.
 */
function renderTrackList(): void {
    const list = $('[data-ft-music-tracklist]');
    if (!list) return;

    const q = trackFilter.trim().toLowerCase();
    let shown = 0;

    const rows = tracks.map((t, i) => {
        const song = songByAudioId.get(t.audio_id);
        // An instrumental row is named for the playlist it belongs to — which in the Everything
        // queue is the track's OWN bucket, not the queue's name. The number column already
        // carries the position, so appending the global index there would read as a track
        // title ("Elvish 347").
        // A titled instrumental now has a name of its own and no longer needs the playlist
        // fallback. Untitled ones still take it — display_name degrades to audio_name server
        // side, and `battle_07` is exactly what the fallback exists to avoid showing.
        const titled = !!t.display_name && t.display_name !== t.audio_name;
        const label = song
            ? esc(song.title) + (song.title_gloss ? ' <span class="text-muted small">“' + esc(song.title_gloss) + '”</span>' : '')
              + (t.bucket_name ? ' <span class="text-muted small">· ' + esc(t.bucket_name) + '</span>' : '')
            : titled
                ? esc(t.display_name as string)
                  + (t.bucket_name ? ' <span class="text-muted small">· ' + esc(t.bucket_name) + '</span>' : '')
                : (t.bucket_name ? esc(t.bucket_name) : esc(bucketDisplayName) + ' ' + (i + 1));

        if (q) {
            // What is VISIBLE in the row, so every match has an on-screen reason.
            const visible = [
                song ? song.title : '',
                song ? song.title_gloss : '',
                // Instrumental titles are on screen now, so they must be searchable — otherwise
                // typing a title you can see returns nothing.
                titled ? (t.display_name as string) : '',
                t.bucket_name || bucketDisplayName,
                song ? (song.language_name || song.language) : '',
            ].join(' ').toLowerCase();
            // Plus the WORDS. This page exists to show lyrics, so "which song has the line about
            // the road" is the obvious thing to type — but a hit in the body has no on-screen
            // reason, hence the explicit tag below rather than a silently-included row.
            const inLyrics = !!song && (song.body.toLowerCase().includes(q)
                                     || song.translation.toLowerCase().includes(q));
            if (!visible.includes(q) && !inLyrics) return '';
            shown++;
            return '<li><button type="button" class="ft-music-track" data-ft-music-track-index="' + i + '">'
                 + '<span class="ft-music-track-no">' + (i + 1) + '</span>'
                 + '<span class="ft-music-track-label">' + label
                 + (song && song.explicit ? ' <span class="badge bg-danger ft-music-explicit">Explicit</span>' : '')
                 + (!visible.includes(q) && inLyrics
                        ? ' <span class="text-muted small ft-music-track-why">· in the lyrics</span>' : '')
                 + '</span>'
                 + '<span class="ft-music-track-time">' + esc(fmtTime(t.audio_seconds || 0)) + '</span>'
                 + '</button></li>';
        }

        shown++;
        return '<li><button type="button" class="ft-music-track" data-ft-music-track-index="' + i + '">'
             + '<span class="ft-music-track-no">' + (i + 1) + '</span>'
             + '<span class="ft-music-track-label">' + label
             + (song && song.explicit ? ' <span class="badge bg-danger ft-music-explicit">Explicit</span>' : '')
             + '</span>'
             + '<span class="ft-music-track-time">' + esc(fmtTime(t.audio_seconds || 0)) + '</span>'
             + '</button></li>';
    }).join('');

    list.innerHTML = rows
        || '<li class="ft-music-track-empty text-muted small">Nothing in this playlist matches “' + esc(trackFilter) + '”.</li>';

    // Say how much of the playlist you are looking at — a filtered list that still claims
    // "22 tracks" in the header above it is lying about what is on screen.
    const countEl = $('[data-ft-music-bucket-count]');
    if (countEl) {
        const n = tracks.length;
        const secs = tracks.reduce((a, t) => a + (Number(t.audio_seconds) || 0), 0);
        countEl.textContent = q
            ? shown + ' of ' + n + ' track' + (n === 1 ? '' : 's') + ' match'
            : n + ' track' + (n === 1 ? '' : 's') + (secs > 0 ? ' · ' + fmtRuntime(secs) : '');
    }

    paint();     // re-apply the current-track highlight to the freshly built rows
}

/**
 * The lyrics panel — ONE song, the one currently playing.
 *
 * It used to render every song in the bucket stacked in a scroller, which meant the words on
 * screen were almost never the words being sung. Following the track makes the panel answer
 * the only question it is there to answer.
 *
 * Tabbed BY LANGUAGE, with the English tab present only when that song actually has a
 * translation. The twenty conlang songs have one, authored in musicforge specs/lyrics/*.md and
 * ingested; the ten Common-language songs do not, because they are already English — so those
 * render a single tab rather than the same text twice. Nothing here generates a translation:
 * the tab shows what was authored or it is absent.
 */
function renderLyricPanel(): void {
    const lyr = $('[data-ft-music-lyrics]');
    if (!lyr) return;
    const col = $('[data-ft-music-lyrics-col]');

    const track = tracks[index];
    const s = track ? songByAudioId.get(track.audio_id) : undefined;

    if (s) {
        if (col) col.style.display = '';
        renderSongInto(lyr, s);
        return;
    }

    // A track IS playing and it has no words — an instrumental in the Everything queue. Showing
    // some other song's lyrics beside it is worse than showing none: the panel's entire promise
    // is that it is the words to what you are hearing. Pull it rather than lie.
    if (track) {
        lyr.innerHTML = '';
        if (col) col.style.display = 'none';
        return;
    }

    // Nothing playing yet: fall back to the first song so a sung playlist does not open on an
    // empty box next to a full track list.
    const first = songs[0];
    if (!first) { lyr.innerHTML = ''; if (col) col.style.display = 'none'; return; }
    if (col) col.style.display = '';
    renderSongInto(lyr, first);
}

function renderSongInto(host: Element, s: MusicSong): void {
    const hasTranslation = s.translation.trim().length > 0;
    if (lyricTab === 'translation' && !hasTranslation) lyricTab = 'lyrics';

    // TABS ARE LABELLED BY LANGUAGE, and by the CATALOG language — "Elvish", not "High Elvish
    // (Elvish)".
    //
    // The spec files write a register ("High Elvish") where the catalog has only the language,
    // so the tab used to carry both and read as a parenthetical apology for a distinction the
    // game does not actually make. We have no Elvish dialects: the dark-elf songs are UMBRAL,
    // which is its own catalog entry, and that is the only split there is. Naming the catalog
    // entry means the tab, the icon beside it and the drawer it opens all say one thing.
    //
    // s.language remains the fallback for a song whose catalog link is missing or soft-deleted,
    // so an unlinked song still names its tongue instead of showing a blank tab.
    const sungLabel = s.language_name || s.language;

    // The tablist is an INNER element, not the whole bar. A tablist may only own tabs, so the
    // language icon beside it has to be its sibling — inside it, a screen reader announces a
    // plain link as part of the tab set and the count of tabs it reads is wrong.
    //
    // THE ICON IS THE LINK. It leads the bar, drawn by the site's own iconMap() from the
    // language's catalog UUID (every language is in FT_ICON_REGISTRY), so it is the same glyph
    // the drawer and every other language reference on the site use. It replaced a trailing
    // "ELVISH ↗" text link that, once the tab said "Elvish" too, was the same word twice on one
    // line. Rendered only when iconMap is actually loaded — a bare <a> with no content would be
    // an invisible click target.
    const langIcon = (s.language_id && s.language_url && typeof iconMap === 'function')
        ? '<a class="ft-music-tabs-icon" href="' + esc(s.language_url) + '"'
          + ' data-ft-music-language-id="' + esc(s.language_id) + '"'
          + ' aria-label="About the ' + esc(sungLabel) + ' language">'
          + iconMap(s.language_id, 22, 22, '') + '</a>'
        : '';

    const tabs =
        '<div class="ft-music-tabs">'
      + langIcon
      + '<div class="ft-music-tablist" role="tablist">'
      + '<button type="button" class="ft-music-tab' + (lyricTab === 'lyrics' ? ' active' : '') + '"'
      + ' role="tab" aria-selected="' + (lyricTab === 'lyrics') + '" data-ft-music-tab="lyrics">'
      + esc(sungLabel) + '</button>'
      + (hasTranslation
            ? '<button type="button" class="ft-music-tab' + (lyricTab === 'translation' ? ' active' : '') + '"'
              + ' role="tab" aria-selected="' + (lyricTab === 'translation') + '" data-ft-music-tab="translation">English</button>'
            : '')
      + '</div>'
      + '</div>';

    const pane = lyricTab === 'translation'
        ? '<pre class="ft-music-song-body">' + esc(s.translation) + '</pre>'
        : '<pre class="ft-music-song-body">' + esc(s.body) + '</pre>';

    host.innerHTML =
        '<article class="ft-music-song" data-ft-music-song-id="' + esc(s.lyric_id) + '">'
      + '<header class="ft-music-song-head">'
      + '<h3 class="h6 m-0">' + esc(s.title)
      + (s.title_gloss ? ' <span class="text-muted fw-normal">“' + esc(s.title_gloss) + '”</span>' : '')
      + (s.explicit ? ' <span class="badge bg-danger ft-music-explicit">Explicit</span>' : '')
      + '</h3>'
      // Title and gloss ONLY. The voice/tempo line ("male + answering chorus, fast") and the
      // about line ("a road-song with a chorus shouting the line back") were direction written
      // for the generator, and reading them back on the listening page put production notes
      // between the reader and the words. They are no longer fetched — see
      // ft_music_bucket_lyrics(). The language now lives in the tabs, so nothing is lost here.
      + '</header>'
      + tabs
      + '<div class="ft-music-song-pane">' + pane + '</div>'
      + '</article>';
}

// ── data ─────────────────────────────────────────────────────────────────────────────────
async function loadIndex(): Promise<void> {
    const status = $('[data-ft-music-status]');
    // Only announce a load that is actually slow — a fast one should go straight to the grid
    // rather than flashing a spinner.
    const slow = setTimeout(() => {
        if (status) { status.textContent = 'Loading the soundtrack…'; status.style.display = ''; }
    }, 250);
    try {
        const res = await fetch(API, { credentials: 'same-origin' });
        const data = await res.json() as BucketIndexPayload;
        clearTimeout(slow);
        if (!res.ok || !data || !Array.isArray(data.groups)) throw new Error('bad payload');
        groups = data.groups;
        if (data.crossfade_ms > 0) crossfadeMs = data.crossfade_ms;
        renderGroups(currentFilter());
    } catch (e) {
        clearTimeout(slow);
        if (status) { status.textContent = 'The soundtrack could not be loaded.'; status.style.display = ''; }
    }
}

const openBucket = (bucket: string): Promise<void> => openQueue(bucket, false);

/** Everything, shuffled — one queue across all 49 buckets rather than a single playlist. */
const openAll = (): Promise<void> => openQueue('', true);

/**
 * Load a queue and start it. `all` picks the cross-bucket payload; otherwise it is one bucket.
 * Shared rather than duplicated because the out-of-order-response guard, the timer disarm and
 * the show/hide dance are the fiddly parts, and two copies of them would drift.
 */
async function openQueue(bucket: string, all: boolean): Promise<void> {
    // Disarm the OLD playlist's handoff before awaiting. Without this, a timer armed against the
    // outgoing playlist can fire during the fetch and advance into tracks[] that is about to be
    // replaced — it self-corrects a moment later, but it audibly jumps a track first.
    clearTimers();
    const token = ++bucketRequest;
    try {
        const url = all ? (API + '?all=1') : (API + '?bucket=' + encodeURIComponent(bucket));
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return;
        const data = await res.json() as BucketPayload;
        if (!data || !Array.isArray(data.tracks) || !data.tracks.length) return;
        // A newer click already won. Applying this response would swap the listener onto a
        // playlist they moved off, because responses can land out of order.
        if (token !== bucketRequest) return;

        allMode = all;
        // "Shuffle everything" IS a shuffle — entering the mode turns it on, and the transport's
        // own shuffle button then reflects that rather than disagreeing with the header.
        if (all) { shuffle = true; persistShuffle(); }
        currentBucket = bucket;
        tracks = data.tracks;
        songs = Array.isArray(data.lyrics) ? data.lyrics : [];
        if (data.crossfade_ms > 0) crossfadeMs = data.crossfade_ms;

        songByAudioId = new Map();
        for (const s of songs) for (const id of s.audio_ids) songByAudioId.set(id, s);

        renderBucket(data);

        const detail = document.getElementById('ft_music_detail');
        const browser = document.getElementById('ft_music_browser');
        const player = document.getElementById('ft_music_player');
        if (detail) detail.style.display = '';
        if (browser) browser.style.display = 'none';
        if (player) player.style.display = '';
        // AFTER the display flip: resetSearchBox asks detailOpen() which list is on screen.
        resetSearchBox();

        // Reflect the open playlist in the URL so it can be linked and survives a reload. The
        // Everything queue gets its own param — the two are mutually exclusive, so whichever is
        // set clears the other rather than leaving a stale one behind for the deep-link reader.
        try {
            const u = new URL(window.location.href);
            if (all) { u.searchParams.delete('playlist'); u.searchParams.set('all', '1'); }
            else     { u.searchParams.delete('all'); u.searchParams.set('playlist', bucket); }
            window.history.replaceState({}, '', u.toString());
        } catch (e) { /* history is a nicety, never a hard dependency */ }

        startPlaylist();
    } catch (e) { /* leave the browser as it was */ }
}

function backToBrowser(): void {
    stopAll();
    tracks = [];
    songs = [];
    songByAudioId = new Map();
    index = -1;
    currentBucket = '';
    allMode = false;
    order = [];
    cursor = 0;
    const detail = document.getElementById('ft_music_detail');
    const browser = document.getElementById('ft_music_browser');
    const player = document.getElementById('ft_music_player');
    if (detail) detail.style.display = 'none';
    if (browser) browser.style.display = '';
    if (player) player.style.display = 'none';
    // Back to the grid, so the box goes back to filtering the grid — and the grid must be
    // repainted unfiltered, or it would still be showing the last query's results.
    resetSearchBox();
    renderGroups('');
    try {
        const u = new URL(window.location.href);
        u.searchParams.delete('playlist');
        u.searchParams.delete('all');
        window.history.replaceState({}, '', u.toString());
    } catch (e) { /* ignore */ }
    paint();     // clears the Everything button's pressed state
}

/** Shuffle is a listening preference, so it outlives the tab — same store as the volume. */
function persistShuffle(): void {
    try { window.localStorage.setItem('ft_music_page_shuffle', shuffle ? '1' : '0'); }
    catch (e) { /* private mode; the toggle still works for this visit */ }
}

function currentFilter(): string {
    const input = document.getElementById('input_search_music') as HTMLInputElement | null;
    return input ? input.value : '';
}

/** True once a playlist or the Everything queue is open — i.e. the grid is not what you see. */
function detailOpen(): boolean {
    const detail = document.getElementById('ft_music_detail');
    return !!detail && detail.style.display !== 'none' && tracks.length > 0;
}

/**
 * Route the one search box to whichever list is actually on screen.
 *
 * The box filtered the bucket GRID unconditionally, so typing while a playlist was open filtered
 * something hidden and looked broken. Which list it drives has to follow what you are looking at.
 */
function applySearch(value: string): void {
    if (detailOpen()) {
        trackFilter = value;
        renderTrackList();
        return;
    }
    trackFilter = '';
    renderGroups(value);
}

/**
 * Empty the box when the list under it is replaced.
 *
 * A query typed against the GRID ("elvish") means something else entirely against a track list,
 * so carrying it across would filter the newly-opened playlist by a term the listener aimed at
 * something else — usually down to nothing, which reads as an empty playlist.
 */
function resetSearchBox(): void {
    trackFilter = '';
    const input = document.getElementById('input_search_music') as HTMLInputElement | null;
    if (input) {
        input.value = '';
        const box = input.closest('.ft-inline-search-root');
        box?.querySelector('[data-ft-inline-search-role="glyph"]')?.classList.remove('d-none');
        box?.querySelector('[data-ft-inline-search-role="clear"]')?.classList.add('d-none');
    }
    syncSearchPlaceholder();
}

/** Say which list the box is about to search, so the routing above is not a hidden mode. */
function syncSearchPlaceholder(): void {
    const input = document.getElementById('input_search_music') as HTMLInputElement | null;
    if (!input) return;
    input.placeholder = detailOpen()
        ? (allMode ? 'Search everything playing' : 'Search this playlist')
        : 'Search the soundtrack';
}

// ── wiring ───────────────────────────────────────────────────────────────────────────────
function init(): void {
    if (!document.getElementById('ft_music_browser')) return;   // not this page

    // Delegated: the grid and the track list are re-rendered wholesale, so per-node listeners
    // would have to be re-attached every paint. One listener on document survives all of it.
    document.addEventListener('click', (ev) => {
        const t = ev.target as HTMLElement | null;
        if (!t) return;

        // Theme tab. Clearing the query is the point: a search overrides the tabs, so leaving
        // the old query in place would make the tab you just pressed appear not to take.
        const groupTab = t.closest('[data-ft-music-group]');
        if (groupTab) {
            activeGroup = groupTab.getAttribute('data-ft-music-group') || '';
            try { window.localStorage.setItem('ft_music_page_group', activeGroup); } catch (e) { /* ignore */ }
            // Was the keyboard driving this? Ask BEFORE the re-render, which destroys the node
            // that currently holds focus.
            const nav = $('[data-ft-music-groupnav]');
            const hadFocus = !!nav && nav.contains(document.activeElement);
            resetSearchBox();
            renderGroups('');
            // renderGroups() rebuilds the bar wholesale, so the focused button is GONE — focus
            // falls to <body> and the next arrow key reaches nothing. Put it back on the tab that
            // is now selected, otherwise arrow navigation works exactly once.
            if (hadFocus) {
                const again = document.querySelector('[data-ft-music-group="' + CSS.escape(activeGroup) + '"]');
                if (again instanceof HTMLElement) again.focus();
            }
            return;
        }

        const card = t.closest('[data-ft-music-bucket]');
        if (card) { void openBucket(card.getAttribute('data-ft-music-bucket') || ''); return; }

        const row = t.closest('[data-ft-music-track-index]');
        if (row) {
            const i = parseInt(row.getAttribute('data-ft-music-track-index') || '-1', 10);
            // syncCursor so the shuffled order resumes FROM the picked track instead of carrying
            // on from wherever the cursor happened to be — otherwise picking a track plays it and
            // then jumps somewhere unrelated.
            if (i >= 0 && i < tracks.length) { index = i; syncCursor(); playCurrent(); }
            return;
        }

        // The language icon on the tab bar → the site's side-info drawer, so the music keeps
        // playing. Following its href would tear down the <audio> decks to read one dictionary
        // entry. It stays a real item.php anchor, so modified clicks (ctrl/cmd/shift/middle, or
        // an explicit target) are left to the browser: "open in a new tab" must keep meaning
        // that, and the link still works if template.js never loaded.
        const lang = t.closest('[data-ft-music-language-id]');
        if (lang) {
            const me = ev as MouseEvent;
            if (me.ctrlKey || me.metaKey || me.shiftKey || me.altKey || me.button !== 0) return;
            // An explicit target means "somewhere other than here" — honour it rather than
            // opening a drawer the author asked to bypass. Nothing sets one today; this keeps
            // that from silently becoming a lie if someone adds target="_blank" later.
            const target = lang.getAttribute('target');
            if (target && target !== '_self') return;
            if (typeof openItemInfo !== 'function') return;   // no template.js → follow the href

            // preventDefault only AFTER the call has been made without throwing, so a broken
            // openItemInfo leaves the anchor's real href intact instead of turning it into a
            // dead click. A REJECTION is different and is swallowed: by then the drawer is
            // already open, and navigating away at that point would stop the music to show an
            // error page — strictly worse than a drawer that failed to fill.
            try {
                const opening = openItemInfo(lang.getAttribute('data-ft-music-language-id') || '');
                ev.preventDefault();
                void Promise.resolve(opening).catch(() => { /* playback is untouched */ });
            } catch (e) { /* not prevented — the browser follows the href */ }
            return;
        }

        const tab = t.closest('[data-ft-music-tab]');
        if (tab) {
            const want = tab.getAttribute('data-ft-music-tab');
            lyricTab = want === 'translation' ? 'translation' : 'lyrics';
            renderLyricPanel();
            return;
        }

        const act = t.closest('[data-ft-music-action]');
        if (!act) return;
        switch (act.getAttribute('data-ft-music-action')) {
            case 'toggle': togglePlay(); break;
            case 'next':   advance(1); break;
            // Prev restarts the current track when you are more than 3 s in — the near-universal
            // transport convention, and it makes a mis-hit recoverable.
            case 'prev': {
                // Settle first so "am I more than 3 s in?" is asked of the deck the listener is
                // actually hearing, not of whichever one happens to be mid-ramp.
                settleFade();
                const d = deckEl(activeDeck);
                if (d && d.currentTime > 3) { try { d.currentTime = 0; } catch (e) { /* ignore */ } scheduleNext(); paint(); }
                else advance(-1);
                break;
            }
            case 'repeat': loopOne = !loopOne; scheduleNext(); paint(); break;
            // Rebuilt around the CURRENT track, so hitting shuffle never interrupts what is
            // playing — only what comes next changes.
            case 'shuffle':
                shuffle = !shuffle;
                buildOrder(index);
                persistShuffle();
                paint();
                break;
            // A toggle: pressed again while the Everything queue is up, it puts you back in the
            // browser rather than silently re-fetching the same 585 tracks.
            case 'shuffle-all':
                if (allMode) backToBrowser();
                else void openAll();
                break;
            case 'back':   backToBrowser(); break;
        }
    });

    // Arrow-key navigation across the theme tabs. role="tablist" PROMISES this — a screen-reader
    // user is told these are tabs and then finds the arrow keys do nothing, which is worse than
    // never having claimed the role. Delegated because the bar is re-rendered on every search.
    const nav = $('[data-ft-music-groupnav]');
    if (nav) {
        nav.addEventListener('keydown', (ev) => {
            const k = (ev as KeyboardEvent).key;
            const step = k === 'ArrowRight' ? 1 : k === 'ArrowLeft' ? -1 : (k === 'Home' ? 0 : (k === 'End' ? -0.5 : NaN));
            if (isNaN(step)) return;
            const tabs = Array.from(nav.querySelectorAll('[data-ft-music-group]')) as HTMLElement[];
            if (!tabs.length) return;
            ev.preventDefault();
            const at = tabs.findIndex((b) => b === document.activeElement);
            const to = k === 'Home' ? 0
                     : k === 'End' ? tabs.length - 1
                     : ((at < 0 ? 0 : at) + (step as number) + tabs.length) % tabs.length;
            const next = tabs[to];
            if (next) { next.focus(); next.click(); }
        });
    }

    // click-to-seek, plus keyboard equivalents since the bar is focusable
    const bar = $('[data-ft-music-progress]');
    if (bar) {
        bar.addEventListener('click', (ev) => {
            const r = bar.getBoundingClientRect();
            if (r.width <= 0) return;
            seekFraction(((ev as MouseEvent).clientX - r.left) / r.width);
        });
        bar.addEventListener('keydown', (ev) => {
            const k = (ev as KeyboardEvent).key;
            const d = deckEl(incomingDeck ?? activeDeck);
            const track = tracks[index];
            if (!d || !track) return;
            const dur = (track.audio_seconds && track.audio_seconds > 0) ? track.audio_seconds : d.duration;
            if (!isFinite(dur) || dur <= 0) return;
            if (k === 'ArrowRight') { ev.preventDefault(); seekFraction(Math.min(1, (d.currentTime + 5) / dur)); }
            else if (k === 'ArrowLeft') { ev.preventDefault(); seekFraction(Math.max(0, (d.currentTime - 5) / dur)); }
            else if (k === ' ' || k === 'Enter') { ev.preventDefault(); togglePlay(); }
        });
    }

    const vol = document.getElementById('ft_music_volume') as HTMLInputElement | null;
    if (vol) {
        // Restore the listener's level. localStorage rather than a cookie: this page has no
        // session and nothing server-side needs to read it.
        try {
            const saved = window.localStorage.getItem('ft_music_page_volume');
            if (saved !== null) { const v = Number(saved); if (!isNaN(v)) { master = Math.max(0, Math.min(1, v)); vol.value = String(Math.round(master * 100)); } }
        } catch (e) { /* ignore */ }
        applyGain();
        vol.addEventListener('input', () => {
            master = Math.max(0, Math.min(1, Number(vol.value) / 100));
            applyGain();
            try { window.localStorage.setItem('ft_music_page_volume', String(master)); } catch (e) { /* ignore */ }
        });
    }

    // Search box + its trailing spyglass/clear icons.
    //
    // includes/search_inline_trailing_icons_inc.php emits the two icons but wires NEITHER — its
    // contract is that the caller sets data-ft-inline-search="1" so the shared helper picks it
    // up, or owns the box itself. This page owns it (like the catalog-list mounts do), and the
    // deliberate consequence is that nothing else touches the input. So the show/hide has to be
    // done here; without it the clear "×" stays d-none forever and the box has no way to reset.
    const search = document.getElementById('input_search_music') as HTMLInputElement | null;
    if (search) {
        const box = search.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = () => {
            if (!glyph || !clear) return;
            const hasText = search.value.trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        search.addEventListener('input', () => { syncTrailing(); applySearch(search.value); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                search.value = '';
                syncTrailing();
                applySearch('');
                search.focus();
            });
        }
        syncTrailing();
        syncSearchPlaceholder();
    }

    // 'ended' is the safety net for the scheduled handoff, not the primary path — if the timer
    // already started the crossfade, the outgoing deck ending must NOT advance a second time.
    for (let i = 0; i < 2; i++) {
        const d = deckEl(i);
        if (!d) continue;
        d.addEventListener('ended', () => {
            // The handoff is scheduled at (duration - crossfade), so the OUTGOING deck reaches
            // its natural end at almost exactly the moment the fade completes. Checking only
            // "is a fade running" loses that race: the fade's last tick can clear fadeTimer and
            // incomingDeck before the already-queued 'ended' runs, and the track then advances
            // twice. Anchoring on deck identity closes it — once the fade completes, activeDeck
            // is the INCOMING deck, so the outgoing deck's late 'ended' is ignored outright.
            if (i !== activeDeck) return;
            if (fadeTimer !== null || incomingDeck !== null) return;   // a crossfade owns this
            if (paused) return;
            advance();
        });
    }

    tickTimer = setInterval(() => { if (!paused) paint(); }, 500);

    // Restore the shuffle preference BEFORE any queue is built, or a deep link would build the
    // identity order and only start shuffling after the listener touched the button.
    try {
        if (window.localStorage.getItem('ft_music_page_shuffle') === '1') shuffle = true;
        // Validated against the payload in renderGroups(), so a stale key from a renamed group
        // falls back to the first tab rather than showing an empty browser.
        activeGroup = window.localStorage.getItem('ft_music_page_group') || '';
    } catch (e) { /* ignore */ }
    paint();

    void loadIndex().then(() => {
        // Deep links: ?playlist=battle opens that playlist, ?all=1 opens the Everything queue.
        // `playlist` wins if somehow both are present — it is the more specific request.
        try {
            const q = new URL(window.location.href).searchParams;
            const want = q.get('playlist');
            if (want) { void openBucket(want); return; }
            const all = q.get('all');
            if (all && all !== '0') void openAll();
        } catch (e) { /* ignore */ }
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

export {};
